import React from 'react';
import Head from 'next/head';
import { NonGatedThemeProvider } from '@lemon-io/ui-kit';

import { getServerProfileResult } from '@internal/api/profile/get-server-profile';
import { ApiContextProvider } from '@internal/components/common/api-context';
import Page from '@internal/components/common/page';
import AppConfig from '@internal/config/app';
import Routes from '@internal/config/routes';
import { Profile } from '@internal/types/profile';
import { REDIRECT_QUERY_PARAM } from '@internal/utils/safe-redirect';
import { withAuthRedirect } from '@internal/utils/with-auth-redirect';

import { JourneyEventsWatcher } from './analytics';
import EnrichmentWatcher from './components/enrichment-watcher';
import FieldStyles from './components/field-styles/field-styles';
import { isSignupIncomplete, isVettingBlockedProfile } from './constants';
import { RoleProvider } from './roles';

export const appPageProps = {
  withoutPadding: true,
  lightBackground: true,
  sideNav: true,
} as const;

const isUnderRoute = (path: string, routes: Routes[]): boolean =>
  routes.some(route => path === route || path.startsWith(`${route}/`));

const redirectTo = (destination: string) => ({
  redirect: { destination, permanent: false },
});

const teamRedirect = (profile: Profile, path: string): string | null => {
  const isPartnerManager = profile.role === 'partner manager';
  const isPartnerDeveloper =
    !isPartnerManager && Boolean(profile.lemonId?.length);

  if (isPartnerDeveloper) {
    return isUnderRoute(path, [
      Routes.GetSetUp,
      Routes.Roles,
      Routes.MyApplications,
      Routes.OpenProjects,
    ])
      ? Routes.Profile
      : null;
  }

  if (isPartnerManager) {
    if (isUnderRoute(path, [Routes.OpenProjects])) return Routes.MyApplications;
    if (
      path !== Routes.MyApplications &&
      path.startsWith(`${Routes.MyApplications}/`)
    ) {
      return Routes.MyApplications;
    }
    return isUnderRoute(path, [Routes.GetSetUp, Routes.Roles, Routes.Profile])
      ? Routes.Developers
      : null;
  }

  return isUnderRoute(path, [
    Routes.GetSetUp,
    Routes.Roles,
    Routes.OpenProjects,
  ])
    ? Routes.Profile
    : null;
};

const candidateRedirect = (
  profile: Profile | null,
  path: string,
  resolvedUrl: string
): string | null => {
  if (!profile) return null;
  if (isSignupIncomplete(profile)) {
    return `${
      Routes.EscapeTheMatrix
    }?${REDIRECT_QUERY_PARAM}=${encodeURIComponent(resolvedUrl)}`;
  }

  if (
    isVettingBlockedProfile(profile) &&
    isUnderRoute(path, [Routes.Roles, Routes.OpenProjects, Routes.GetSetUp])
  ) {
    return Routes.MyApplications;
  }

  return null;
};

export const getAppServerSideProps = withAuthRedirect(async context => {
  const { profile, unauthorized } = await getServerProfileResult(context);
  if (unauthorized) {
    return redirectTo(`${process.env.APP_LEMON_URL as string}/auth/login`);
  }

  const path = context.resolvedUrl.split('?')[0];
  const destination = profile?.teamId
    ? teamRedirect(profile, path)
    : candidateRedirect(profile, path, context.resolvedUrl);

  return destination ? redirectTo(destination) : { props: appPageProps };
});

const AppRoute: React.FC<{
  title?: string;
  children: React.ReactNode;
}> = ({ title = AppConfig.Title, children }) => (
  <Page>
    <Head>
      <title>{title}</title>
    </Head>
    <ApiContextProvider>
      <NonGatedThemeProvider>
        <FieldStyles />
        <RoleProvider>
          <EnrichmentWatcher />
          <JourneyEventsWatcher />
          {children}
        </RoleProvider>
      </NonGatedThemeProvider>
    </ApiContextProvider>
  </Page>
);

export default AppRoute;
